import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CheckCircle2, Package, Receipt, X } from 'lucide-react';
import type {
  AbsenceDecisionOption,
  AbsenceDecisionPreview,
} from '../../services/AppointmentStatusService';

interface AbsenceDecisionModalProps {
  isOpen: boolean;
  preview: AbsenceDecisionPreview | null;
  loading: boolean;
  onConfirm: (absenceType: 'justified' | 'unjustified', optionId: string | null) => Promise<void>;
  onCancel: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return 'não informado';
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const translateOptionDescription = (value?: string | null) => {
  if (!value) return '';
  const translations: Record<string, string> = {
    package: 'pacote',
    recurring: 'recorrente',
    insurance: 'convênio',
    company: 'empresa',
    custom: 'personalizado',
    fixed_credits: 'créditos fixos',
    period_credits: 'créditos por período',
    period_access: 'acesso por período',
    per_session: 'por sessão',
    rental_period: 'locação por período',
  };

  return Object.entries(translations).reduce(
    (translated, [term, label]) => translated.replace(new RegExp(`\\b${term}\\b`, 'gi'), label),
    value
  );
};

const periodLabel = (option: AbsenceDecisionOption) => {
  if (option.countingPeriodType === 'calendar_month') return 'mês';
  if (option.countingPeriodType === 'calendar_year') return 'ano';
  return `período de ${formatDate(option.periodFrom)} a ${formatDate(option.periodUntil)}`;
};

const impactLabel = (option: AbsenceDecisionOption) => {
  if (!option.isChargeable) {
    return `Sem cobrança: a regra começa na ${option.chargeFromAbsence}ª falta.`;
  }
  if (option.financialImpact === 'credit_consumption') return 'Consumirá 1 crédito do pacote ao confirmar.';
  if (option.financialImpact === 'monetary_charge') return `Gerará cobrança de ${formatMoney(option.amount)} ao confirmar.`;
  if (option.financialImpact === 'both') return `Consumirá crédito e gerará cobrança de ${formatMoney(option.amount)} ao confirmar.`;
  return 'Sem impacto financeiro.';
};

export const AbsenceDecisionModal: React.FC<AbsenceDecisionModalProps> = ({
  isOpen,
  preview,
  loading,
  onConfirm,
  onCancel,
}) => {
  const [absenceType, setAbsenceType] = useState<'justified' | 'unjustified'>('unjustified');
  const [selectedOptionId, setSelectedOptionId] = useState<string>('');
  const [acknowledged, setAcknowledged] = useState(false);

  const selectedOption = useMemo(
    () => preview?.options.find((option) => option.optionId === selectedOptionId) || null,
    [preview, selectedOptionId]
  );

  useEffect(() => {
    if (!isOpen) return;
    setAbsenceType('unjustified');
    setSelectedOptionId('');
    setAcknowledged(false);
  }, [isOpen, preview?.appointmentId]);

  if (!isOpen || !preview) return null;

  const requiresAcknowledgement = absenceType === 'unjustified' && Boolean(selectedOption?.isChargeable || selectedOption?.requiresFollowUp);
  const canConfirm = absenceType === 'justified'
    || (Boolean(selectedOption?.available) && (!requiresAcknowledgement || acknowledged));

  const selectType = (type: 'justified' | 'unjustified') => {
    setAbsenceType(type);
    setAcknowledged(false);
    if (type === 'justified') setSelectedOptionId('');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Registrar falta do paciente</h3>
            <p className="mt-1 text-sm text-gray-600">
              {preview.patientName} · {formatDate(preview.appointmentDate)}
            </p>
          </div>
          <button type="button" onClick={onCancel} disabled={loading} className="text-gray-400 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-800">A falta foi justificada?</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => selectType('justified')}
                className={`rounded-lg border-2 p-4 text-left ${absenceType === 'justified' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <span className="font-semibold text-gray-900">Falta justificada</span>
                <span className="mt-1 block text-sm text-gray-600">Registra a falta sem cobrança ou consumo de crédito.</span>
              </button>
              <button
                type="button"
                onClick={() => selectType('unjustified')}
                className={`rounded-lg border-2 p-4 text-left ${absenceType === 'unjustified' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <span className="font-semibold text-gray-900">Falta não justificada</span>
                <span className="mt-1 block text-sm text-gray-600">Aplica a política efetiva e solicita a origem financeira.</span>
              </button>
            </div>
          </div>

          {absenceType === 'justified' ? (
            <div className="flex gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <p>O agendamento será marcado como falta justificada, sem gerar pendência financeira.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <CalendarDays className="mt-0.5 h-5 w-5 shrink-0" />
                <p>
                  Esta é, em princípio, a <strong>{preview.defaultAbsenceNumber}ª falta</strong>. A posição final e a cobrança
                  podem variar conforme a política do contrato escolhido.
                </p>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-800">Como esta sessão seria utilizada?</p>
                <div className="space-y-2">
                  {preview.options.map((option) => {
                    const selected = selectedOptionId === option.optionId;
                    const Icon = option.origin === 'pacote' ? Package : option.origin === 'avulsa' ? Receipt : AlertTriangle;
                    return (
                      <button
                        key={option.optionId}
                        type="button"
                        disabled={!option.available || loading}
                        onClick={() => {
                          setSelectedOptionId(option.optionId);
                          setAcknowledged(false);
                        }}
                        className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'} disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-65`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold text-gray-900">{option.title}</span>
                              <span className="text-xs font-medium text-gray-600">
                                {option.absenceNumber}ª falta no {periodLabel(option)}
                              </span>
                            </div>
                            {option.subtitle && <p className="mt-1 text-sm text-gray-600">{translateOptionDescription(option.subtitle)}</p>}
                            <p className={`mt-2 text-sm font-medium ${option.isChargeable ? 'text-amber-800' : 'text-green-700'}`}>
                              {impactLabel(option)}
                            </p>
                            {option.blocker && <p className="mt-2 text-sm text-red-700">{option.blocker}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedOption?.requiresFollowUp && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  A falta será registrada, mas ficará pendente de regularização porque não há contrato ou pacote elegível.
                </div>
              )}

              {requiresAcknowledgement && (
                <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    className="mt-1"
                  />
                  <span>Estou ciente da política apresentada e confirmo a origem selecionada para o processamento financeiro.</span>
                </label>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button type="button" onClick={onCancel} disabled={loading} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Voltar
          </button>
          <button
            type="button"
            disabled={!canConfirm || loading}
            onClick={() => onConfirm(absenceType, absenceType === 'justified' ? null : selectedOptionId)}
            className="rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Registrando...' : 'Confirmar falta'}
          </button>
        </div>
      </div>
    </div>
  );
};
